import {
  ApartmentOutlined,
  ApiOutlined,
  AppstoreOutlined,
  AuditOutlined,
  DatabaseOutlined,
  FileTextOutlined,
  FlagOutlined,
  FunnelPlotOutlined,
  GlobalOutlined,
  InboxOutlined,
  LinkOutlined,
  NumberOutlined,
  PartitionOutlined,
  ProfileOutlined,
  RobotOutlined,
  SafetyOutlined,
  SettingOutlined,
  StarOutlined,
  TagsOutlined,
  TeamOutlined,
  ThunderboltOutlined,
  UserOutlined,
} from '@ant-design/icons';
import { history, useAccess, useIntl, useLocation } from '@umijs/max';
import { useTheme } from '@zhiyue/core';
import { Button, Menu, Tooltip } from 'antd';
import React from 'react';
import { SYSTEM_CONFIG_ITEMS } from '@/modules/zy-crm/components/system-config/registry';
import { getSettingsItems } from '@/navigation';
import { buildCrmMenuData } from '@/utils/menuData';
export function SettingsButton() {
  const access = useAccess() as Record<string, boolean>;
  const intl = useIntl();
  const target = getSettingsItems(access).flatMap(
    (group) => group.children || [],
  )[0]?.path;
  if (!target) return null;
  const title = intl.formatMessage({ id: 'menu.zy-crm.settings' });
  return (
    <Tooltip title={title}>
      <Button
        type="text"
        aria-label={title}
        icon={<SettingOutlined />}
        onClick={() => history.push(target)}
      />
    </Tooltip>
  );
}
const SETTINGS_ICONS = [
  SafetyOutlined,
  TagsOutlined,
  ThunderboltOutlined,
  AuditOutlined,
  FileTextOutlined,
  RobotOutlined,
  DatabaseOutlined,
  InboxOutlined,
  PartitionOutlined,
  ApartmentOutlined,
  GlobalOutlined,
  TeamOutlined,
  UserOutlined,
  LinkOutlined,
  StarOutlined,
  FunnelPlotOutlined,
  FlagOutlined,
  AppstoreOutlined,
  ApiOutlined,
  ProfileOutlined,
  NumberOutlined,
];

// Match the complete path boundary so dictionary item routes retain their parent.
export function getSelectedSettingsPath(
  groups: ReturnType<typeof getSettingsItems>,
  pathname: string,
) {
  return groups
    .flatMap((group) => group.children || [])
    .filter(
      (item) =>
        item.path &&
        (pathname === item.path || pathname.startsWith(`${item.path}/`)),
    )
    .sort((a, b) => (b.path?.length || 0) - (a.path?.length || 0))[0]?.path;
}

export function SettingsNavigation({
  collapsed = false,
}: {
  collapsed?: boolean;
}) {
  const access = useAccess() as Record<string, boolean>;
  const location = useLocation();
  const intl = useIntl();
  const { navTheme } = useTheme();
  const iconByPath = new Map(
    SYSTEM_CONFIG_ITEMS.map((item, index) => [
      item.path,
      SETTINGS_ICONS[index] || SettingOutlined,
    ]),
  );
  const groups = buildCrmMenuData(getSettingsItems(access));
  const menuGroups = groups.map((group) => ({
    key: group.key || '',
    type: 'group' as const,
    label: group.title,
    children: group.children?.map((item) => {
      const Icon = iconByPath.get(item.path || '') || SettingOutlined;
      return {
        key: item.path || '',
        label: item.title,
        title: item.title,
        icon: collapsed ? <Icon /> : undefined,
      };
    }),
  }));
  return (
    <div className="crm-sider-menu-content crm-settings-navigation">
      <Menu
        aria-label={intl.formatMessage({ id: 'menu.zy-crm.settings' })}
        mode="inline"
        theme={navTheme === 'realDark' ? 'dark' : 'light'}
        inlineCollapsed={collapsed}
        selectedKeys={[
          getSelectedSettingsPath(groups, location.pathname) ||
            location.pathname,
        ]}
        items={
          collapsed
            ? menuGroups.flatMap((group) => group.children || [])
            : menuGroups
        }
        onClick={({ key }) => {
          if (key !== location.pathname) history.push(key);
        }}
      />
    </div>
  );
}
